import { graphql } from 'client/gql';
import type { CollaborationStatus } from 'client/gql/graphql';
import { CollaborationsModal } from 'components/Collaborations/modals/CollaborationsModal';
import { CollaborationsTable } from 'components/Collaborations/tables/CollaborationsTable';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { BpSubpage } from '../../../components/BpSubpage/BpSubpage';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { showErrorToast } from '../../../utils/showErrorToast';
import { showSuccessToast } from '../../../utils/showSuccessToast';

const CreateCollaborationMutationDocument = graphql(`
  mutation CreateCollaborationInSettings($uuid: ID!) {
    createCollaboration(uuid: $uuid) {
      uuid
      __typename
    }
  }
`);
const DeleteCollaborationMutationDocument = graphql(`
  mutation DeleteCollaboration($uuid: ID!) {
    deleteCollaboration(uuid: $uuid) {
      entitiesDeleted
    }
  }
`);

const SettingsCollaborationsSubpageQueryDocument = graphql(`
  query SettingsCollaborationsSubpageData($organizationUuid: ID!) {
    ...CollaborationsTableFields
  }
`);

export type Collaboration = {
  uuid: string;
  name: string;
  collaborationStatus: CollaborationStatus;
  sourceOrganization: string;
};

export const SettingsCollaborationsSubpage: FC = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const [{ data }] = useQuery({
    query: SettingsCollaborationsSubpageQueryDocument,
    variables: {
      organizationUuid: organizationUuid,
    },
  });

  const permissions = usePermissionChecker();

  const [modalOpen, setModalOpen] = useState(false);

  const [, createCollaboration] = useMutation(CreateCollaborationMutationDocument);
  const [, deleteCollaboration] = useMutation(DeleteCollaborationMutationDocument);

  const handleAccept = (collaborationUuid: string) => {
    createCollaboration({ uuid: collaborationUuid })
      .then(() => {
        showSuccessToast(t('common.saved'));
      })
      .catch((resp) => {
        showErrorToast(resp.error);
      });
  };

  const handleCancel = (collaborationUuid: string) => {
    deleteCollaboration({ uuid: collaborationUuid })
      .then((resp) => {
        if ((resp.data?.deleteCollaboration.entitiesDeleted ?? 0) > 0) {
          showSuccessToast(t('delete.deleted', { type: t('collaborations.titleSingular') }));
        } else {
          showErrorToast(
            resp.error ?? {
              message: t('common.error'),
              name: '',
              graphQLErrors: [],
            },
          );
        }
      })
      .catch((resp) => {
        showErrorToast(resp.error);
      });
  };

  return (
    <BpSubpage
      isForbidden={
        !permissions?.canAcceptCollaboration({ uuid: organizationUuid }) &&
        !permissions?.canCreateCollaboration({ uuid: organizationUuid })
      }
    >
      <CollaborationsTable
        data={data}
        onAdd={() => setModalOpen(true)}
        onAccept={handleAccept}
        onCancel={handleCancel}
      />
      <CollaborationsModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </BpSubpage>
  );
};
